import { useEffect, useState } from 'react';
import {
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useBranding } from '../theme/BrandingProvider';

const FONTS = [
  { value: 'Segoe UI', label: 'Segoe UI (Standard)' },
  { value: 'Outfit', label: 'Outfit (CL-Reporting)' },
];

export function CorporateDesignSettings() {
  const api = useApi();
  const branding = useBranding();
  const current = branding.branding;

  const [appName, setAppName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [fontFamily, setFontFamily] = useState('Segoe UI');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (current) {
      setAppName(current.appName);
      setPrimaryColor(current.primaryColor);
      setFontFamily(current.fontFamily || 'Segoe UI');
    }
  }, [current]);

  if (!current) {
    return <Spinner size="tiny" label="Lade…" />;
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateBranding({
        appName,
        primaryColor,
        secondaryColor: current!.secondaryColor,
        accentColor: current!.accentColor,
        logoUrl: current!.logoUrl,
        fontFamily,
      });
      branding.reload();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: '520px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      <Field label="App-Name">
        <Input value={appName} onChange={(_, d) => setAppName(d.value)} />
      </Field>

      <Field label="Primärfarbe (Hex)" hint="z. B. #D84F22">
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
          <Input value={primaryColor} onChange={(_, d) => setPrimaryColor(d.value)} style={{ flex: 1 }} />
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              backgroundColor: primaryColor,
              border: '1px solid var(--colorNeutralStroke2)',
              flexShrink: 0,
            }}
          />
        </div>
      </Field>

      <Field label="Schriftart">
        <Dropdown
          value={FONTS.find((f) => f.value === fontFamily)?.label ?? fontFamily}
          selectedOptions={[fontFamily]}
          onOptionSelect={(_, d) => d.optionValue && setFontFamily(d.optionValue)}
        >
          {FONTS.map((f) => (
            <Option key={f.value} value={f.value} text={f.label}>
              {f.label}
            </Option>
          ))}
        </Dropdown>
      </Field>

      <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center' }}>
        <Button appearance="primary" onClick={save} disabled={saving}>
          {saving ? 'Speichere…' : 'Speichern'}
        </Button>
        {saved ? <Caption1 style={{ color: tokens.colorPaletteGreenForeground1 }}>Gespeichert ✓</Caption1> : null}
      </div>

      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        Schriftart und Farben gelten zentral für alle Nutzer; die Ansicht aktualisiert sich nach dem Speichern.
      </Caption1>
    </div>
  );
}
